import React, { useEffect, useState } from "react";
import {
  Card,
  Flex,
  Button,
  TextInput,
  NumberInput,
  Select,
  SelectItem,
  Switch,
  Text,
  Badge,
} from "@tremor/react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import * as devicesAPI from "../../../services/device";
import Toast from "../../../components/Toast";

const TYPES = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "date", label: "Date" },
  { value: "binary", label: "Binary (0/1)" },
];

const MAX_ATTRIBUTES = 50;

// Stable client-side row ids: row objects get replaced on every edit (see updateRow),
// so identity comparisons need a durable id rather than object reference or array index.
let nextRowId = 1;
const newRowId = () => nextRowId++;

// Convert an ISO string / Date into the value a <input type="datetime-local"> expects (local time, minute precision).
const toLocalInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert a datetime-local string back to ISO (UTC) for the API.
const fromLocalInput = (local) => {
  if (!local) return "";
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const defaultValueFor = (type) => {
  switch (type) {
    case "number": return 0;
    case "boolean": return false;
    case "binary": return 0;
    case "date": return new Date().toISOString();
    default: return "";
  }
};

// Rows keep the API shape plus a client-only `saved` flag (exists on the server) and `error` message.
// `savedKey` is the key the row is known by on the server, captured when the row is loaded/saved and
// never touched by editing the visible `key` field. Deletes must use it (not the possibly-since-edited
// `key`) so we remove the row the server actually has. Unsaved rows (from onAdd) have no server-side
// key yet, so savedKey is null.
// After a successful write we mutate `device.attributes` and pass the same object to `onUpdate`, the
// pattern SettingsForm already uses, so the parent state updates without re-seeding rows (which would
// drop unsaved edits on other rows). The effect below only re-seeds when the parent hands us a new device.
const toRows = (attributes) =>
  (attributes || []).map((a) => ({
    rid: newRowId(), key: a.key, type: a.type, value: a.value, saved: true, savedKey: a.key, error: null,
  }));

export default function AttributesForm({ device, onUpdate }) {
  const [rows, setRows] = useState(toRows(device.attributes));
  const [listError, setListError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    setRows(toRows(device.attributes));
    setListError(null);
  }, [device]);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch, error: null } : r)));
    setListError(null);
  };

  const onTypeChange = (index, type) => {
    updateRow(index, { type, value: defaultValueFor(type) });
  };

  const onAdd = () => {
    if (rows.length >= MAX_ATTRIBUTES) return;
    setRows((prev) => [...prev, { rid: newRowId(), key: "", type: "string", value: "", saved: false, savedKey: null, error: null }]);
  };

  const onRemove = async (index) => {
    const row = rows[index];
    if (!row.saved) {
      setRows((prev) => prev.filter((r) => r.rid !== row.rid));
      return;
    }
    try {
      // Delete by the key the server knows this row by, not `row.key`, which may
      // have been edited locally (and not yet saved) since the row was loaded.
      const remaining = await devicesAPI.removeAttribute(device._id, row.savedKey);
      // Only drop the deleted row; unsaved edits on the other rows stay.
      // Filter by stable row id (not index or object reference) since concurrent
      // deletes/edits can shift positions or replace row objects before this
      // async call resolves.
      setRows((prev) => prev.filter((r) => r.rid !== row.rid));
      device.attributes = remaining;
      onUpdate(device);
      setToast({ show: true, message: `Removed "${row.savedKey}"`, type: "success" });
    } catch (err) {
      console.error("Error removing attribute:", err);
      setToast({ show: true, message: err.error || "Failed to remove attribute", type: "error" });
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setListError(null);
    try {
      const payload = rows.map(({ key, type, value }) => ({ key, type, value }));
      const saved = await devicesAPI.putAttributes(device._id, payload);
      setRows(toRows(saved));
      device.attributes = saved;
      onUpdate(device);
      setToast({ show: true, message: "Attributes saved", type: "success" });
    } catch (err) {
      console.error("Error saving attributes:", err);
      if (err.status === 400 && Array.isArray(err.details)) {
        const listLevel = err.details.filter((d) => d.index === -1).map((d) => d.message);
        setListError(listLevel.length ? listLevel.join(". ") : null);
        setRows((prev) =>
          prev.map((r, i) => {
            const d = err.details.find((x) => x.index === i);
            return d ? { ...r, error: d.message } : r;
          })
        );
        setToast({ show: true, message: "Please fix the highlighted attributes", type: "error" });
      } else {
        setToast({ show: true, message: err.error || "Failed to save attributes", type: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderValueInput = (row, index) => {
    switch (row.type) {
      case "number":
        return (
          <NumberInput
            value={row.value}
            onValueChange={(v) => updateRow(index, { value: v })}
            enableStepper={false}
          />
        );
      case "boolean":
        return (
          <Flex justifyContent="start" className="gap-2 h-full items-center">
            <Switch checked={row.value === true} onChange={(v) => updateRow(index, { value: v })} />
            <Text>{row.value === true ? "true" : "false"}</Text>
          </Flex>
        );
      case "binary":
        return (
          <Flex justifyContent="start" className="gap-2 h-full items-center">
            <Switch checked={row.value === 1} onChange={(v) => updateRow(index, { value: v ? 1 : 0 })} />
            <Text>{row.value === 1 ? "1" : "0"}</Text>
          </Flex>
        );
      case "date":
        return (
          <input
            type="datetime-local"
            className="w-full rounded-tremor-default border border-tremor-border dark:border-dark-tremor-border px-3 py-2 text-sm shadow-tremor-input"
            value={toLocalInput(row.value)}
            onChange={(e) => updateRow(index, { value: fromLocalInput(e.target.value) })}
          />
        );
      default:
        return (
          <TextInput
            value={row.value ?? ""}
            placeholder="Value"
            onChange={(e) => updateRow(index, { value: e.target.value })}
          />
        );
    }
  };

  return (
    <Card>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={3000}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <form onSubmit={onSave}>
        {listError && (
          <Text className="mb-3 text-red-600">{listError}</Text>
        )}

        {rows.length === 0 && (
          <Text className="mb-4 text-gray-500 dark:text-gray-400">
            No attributes yet. Add key/value metadata such as timezone, location or firmware version.
          </Text>
        )}

        <div className="space-y-3">
          {rows.map((row, index) => (
            <div key={row.rid}>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                <div className="sm:col-span-4">
                  <TextInput
                    value={row.key}
                    placeholder="key"
                    error={Boolean(row.error)}
                    onChange={(e) => updateRow(index, { key: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-3">
                  <Select value={row.type} onValueChange={(v) => onTypeChange(index, v)}>
                    {TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                <div className="sm:col-span-4">{renderValueInput(row, index)}</div>
                <div className="sm:col-span-1 flex justify-end">
                  <button
                    type="button"
                    aria-label={`Remove ${row.key || "attribute"}`}
                    onClick={() => onRemove(index)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              {row.error && (
                <Text className="mt-1 text-xs text-red-600">{row.error}</Text>
              )}
            </div>
          ))}
        </div>

        <Flex justifyContent="between" className="mt-4">
          <Flex justifyContent="start" className="gap-2">
            <Button
              type="button"
              variant="secondary"
              icon={PlusIcon}
              onClick={onAdd}
              disabled={rows.length >= MAX_ATTRIBUTES}
            >
              Add attribute
            </Button>
            <Badge color="gray" size="xs">{rows.length} / {MAX_ATTRIBUTES}</Badge>
          </Flex>
          <Button type="submit" loading={isSaving} disabled={isSaving}>
            Save
          </Button>
        </Flex>
      </form>
    </Card>
  );
}
